import { z } from 'zod';
import { normalizeUsPhone, US_PHONE_INVALID_MESSAGE } from '../format/phone.js';
import { normalizeUsState, US_STATE_INVALID_MESSAGE } from '../geo/us-states.js';
import { normalizeUsZip, US_ZIP_INVALID_MESSAGE } from '../geo/zip.js';
import { EMPLOYEE_COUNT_MAX, IMPORT_ROWS_MAX, TEXT_LIMITS } from '../limits.js';
import { AccountSourceSchema } from './account.js';
import { normalizeIndustry } from './industry.js';
import { ContactIdSchema, EmailSchema, IDSchema, IsoTimestampSchema } from './shared.js';
/**
 * T2-009 — Shared schemas for the bulk-CSV-import workflow.
 *
 * `BulkImportRowInputSchema` validates one parsed CSV row's normalized
 * fields (case-insensitive header normalization happens server-side
 * before Zod parses).
 *
 * `BulkImportDraftPayloadSchema` is the shape stored in
 * `bulk_import_drafts.draftPayload` and returned by `POST
 * /api/v1/imports/leads`. The frontend (T2-010) renders the payload
 * into the per-row decision UI.
 *
 * **Not catalog entities** — the draft is a transient operational
 * record, not part of the data-model entity catalog. Defined here for
 * frontend/backend alignment, not for the Zod-Drizzle drift checker.
 */
// ─── Per-row input ─────────────────────────────────────────────────────────
/**
 * QA 2026-10-01 F-04 — an optional CSV cell stored in one normalized form
 * (state code, ZIP, phone). Blank becomes absent; an invalid value is a Zod
 * issue, so the row lands as `invalid` with a plain validation message instead
 * of passing through to the account as typed.
 */
function normalizedCell(maxLength, normalize, message) {
    return z
        .string()
        .max(maxLength)
        .optional()
        .transform((v, ctx) => {
        if (v === undefined)
            return undefined;
        const trimmed = v.trim();
        if (trimmed === '')
            return undefined;
        const normalized = normalize(trimmed);
        if (normalized === null) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
            return z.NEVER;
        }
        return normalized;
    });
}
/**
 * Normalized fields parsed out of one CSV row. Required: companyName.
 * Everything else optional — the dedup-and-decide UI surfaces missing
 * fields per row so the rep can patch them at commit time.
 */
export const BulkImportRowInputSchema = z.object({
    companyName: z.string().min(1).max(200),
    // QA 2026-10-01 F-08 / F-22 — the same bounds as POST /api/v1/accounts.
    companyDomain: z.string().max(TEXT_LIMITS.domain).optional(),
    // QA 2026-10-01 F-01: "Aerospace & Defense", "non-profit" and the like map
    // to the shared industry codes; text that matches nothing is kept as typed
    // (nothing is lost; it shows as typed on the account).
    industry: z
        .string()
        .max(TEXT_LIMITS.industry)
        .optional()
        .transform((v) => {
        if (v === undefined)
            return undefined;
        const trimmed = v.trim();
        if (trimmed === '')
            return undefined;
        return normalizeIndustry(trimmed) ?? trimmed;
    }),
    employeeCount: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX).optional(),
    // RIG-399 — account address (Apollo Company Address/City/State/Zip). These
    // already exist on the accounts table; exposing them to import lets the
    // Apollo export's company location flow through to the Account.
    addressLine1: z.string().max(200).optional(),
    addressLine2: z.string().max(200).optional(),
    city: z.string().max(100).optional(),
    // QA 2026-10-01 F-04 — normalized ("AL", "35203-1234"); invalid values
    // make the row invalid.
    mailingState: normalizedCell(TEXT_LIMITS.stateInput, normalizeUsState, US_STATE_INVALID_MESSAGE),
    postalCode: normalizedCell(TEXT_LIMITS.postal, normalizeUsZip, US_ZIP_INVALID_MESSAGE),
    // RIG-399 — account (company) phone, distinct from the contact's phone.
    accountPhone: normalizedCell(50, normalizeUsPhone, US_PHONE_INVALID_MESSAGE),
    customerTimezone: z.string().max(TEXT_LIMITS.timezone).optional(),
    source: AccountSourceSchema.optional(),
    sourceCampaign: z.string().max(TEXT_LIMITS.shortText).optional(),
    sourceContactName: z.string().max(TEXT_LIMITS.name).optional(),
    // Primary contact fields. Most CSVs carry contact info inline with
    // company info (one row = one company + one contact); the importer
    // creates the Account + the primary Contact in a single per-row
    // commit.
    contactName: z.string().max(TEXT_LIMITS.name).optional(),
    contactEmail: z.string().max(TEXT_LIMITS.email).email().optional(),
    contactPhone: normalizedCell(50, normalizeUsPhone, US_PHONE_INVALID_MESSAGE),
    contactTitle: z.string().max(TEXT_LIMITS.title).optional(),
});
// ─── Per-row dedup match ───────────────────────────────────────────────────
/**
 * One candidate match surfaced for a row by the dedup query. Either an
 * Account match (by `companyDomainNormalized`) or a Contact match (by
 * `email_normalized`) — the row's commit decision picks "create new"
 * vs "merge into <accountId>".
 */
export const BulkImportRowMatchSchema = z.object({
    accountId: IDSchema,
    companyName: z.string(),
    companyDomain: z.string().nullable(),
    matchedOn: z.enum(['domain', 'contact_email']),
    /** Set when matchedOn === 'contact_email'. */
    matchedContactId: ContactIdSchema.optional(),
});
// ─── Per-row draft entry ───────────────────────────────────────────────────
export const BulkImportRowStatusSchema = z.enum([
    'valid', // parsed + validated; no dedup matches; will create new
    'duplicate', // parsed + validated; dedup matches found
    'invalid', // Zod validation failed
    'error', // server-side parse error (malformed CSV row, etc.)
]);
export const BulkImportDraftRowSchema = z.object({
    /** 1-indexed row number from the CSV (data row, not header). */
    rowIndex: z.number().int().positive(),
    status: BulkImportRowStatusSchema,
    /** Parsed-and-normalized fields. Present when status != 'error'. */
    input: BulkImportRowInputSchema.optional(),
    /** Per-row dedup matches. Empty when status === 'valid' or 'invalid'. */
    matches: z.array(BulkImportRowMatchSchema).default([]),
    /** Zod validation errors flattened into a string array. */
    validationErrors: z.array(z.string()).default([]),
    /** Server-side error message (status === 'error'). */
    errorMessage: z.string().optional(),
});
// ─── Top-level draft payload ───────────────────────────────────────────────
export const BulkImportSummarySchema = z.object({
    totalRows: z.number().int().nonnegative(),
    validCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
    invalidCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    /**
     * Count of data rows dropped because the file exceeded MAX_ROWS and was
     * truncated before parsing. 0 when the file was within the cap.
     */
    droppedRowCount: z.number().int().nonnegative().default(0),
});
export const BulkImportDraftPayloadSchema = z.object({
    rows: z.array(BulkImportDraftRowSchema),
    summary: BulkImportSummarySchema,
});
// ─── Endpoint response ─────────────────────────────────────────────────────
export const BulkImportDraftResponseSchema = z.object({
    importId: IDSchema,
    uploadedBy: EmailSchema,
    filename: z.string(),
    totalRows: z.number().int().nonnegative(),
    draftPayload: BulkImportDraftPayloadSchema,
    createdAt: IsoTimestampSchema,
    expiresAt: IsoTimestampSchema,
});
// ─── T2-010A — commit endpoint request/response shapes ─────────────────────
export const BulkImportRowDecisionSchema = z.object({
    rowIndex: z.number().int().positive().max(IMPORT_ROWS_MAX),
    action: z.enum(['create', 'merge', 'skip']),
    /** Required when action === 'merge'; ignored otherwise. */
    existingAccountId: IDSchema.optional(),
    /** Optional even when action === 'merge'. If present, marks an existing Contact under the merged Account. If absent on merge, a new Contact is created under the existing Account (when the row carries a contact). */
    existingContactId: ContactIdSchema.optional(),
});
export const BulkImportCommitRequestSchema = z.object({
    rowDecisions: z.array(BulkImportRowDecisionSchema).min(1).max(IMPORT_ROWS_MAX),
});
export const BulkImportRowOutcomeSchema = z.object({
    rowIndex: z.number().int().positive(),
    status: z.enum(['created', 'merged', 'skipped', 'errored']),
    accountId: IDSchema.optional(),
    contactId: ContactIdSchema.optional(),
    error: z.string().optional(),
});
export const BulkImportCommitResponseSchema = z.object({
    importId: IDSchema,
    summary: z.object({
        newAccounts: z.number().int().nonnegative(),
        mergedRows: z.number().int().nonnegative(),
        skippedRows: z.number().int().nonnegative(),
        errored: z.number().int().nonnegative(),
    }),
    perRow: z.array(BulkImportRowOutcomeSchema),
});
//# sourceMappingURL=bulk-import.js.map